import { Sparkles } from "lucide-react";
import { useState } from "react";
import { AiJobProgress } from "@/components/ai/AiJobProgress";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  useActiveAiJobs,
  useAlbumReviewStatus,
  useRequestAlbumReview,
} from "@/hooks/use-ai-review";
import { ApiError } from "@/lib/api-client";
import type { AlbumReviewStatus } from "@/schemas/ai-review";

/** 503 (AI not set up) and 429 (today's limit): the message to show under the button. */
export function blockingMessage(error: Error | null): string | null {
  return error instanceof ApiError && (error.status === 503 || error.status === 429)
    ? error.message
    : null;
}

/** Only 503 keeps the button off: the limit in a 429 lifts tomorrow, and a click just gets it again. */
export function isNotSetUp(error: Error | null): boolean {
  return error instanceof ApiError && error.status === 503;
}

interface AlbumCheckPlan {
  label: "Check with AI" | "Check again";
  /** What to ask before sending, with how many photos it spends; null sends at once. */
  confirm: { count: number; text: string } | null;
}

const photoCount = (n: number) => (n === 1 ? "the 1 photo" : `all ${n} photos`);

/**
 * What the album button does. An album already checked (it has a summary, or every photo the AI
 * can read has been read) is "Check again", and asks first, naming the photos it would spend:
 * every readable photo, or only the unread ones when there are any (the server reads those).
 * An album never checked is one click.
 */
export function albumCheckPlan(status: AlbumReviewStatus): AlbumCheckPlan {
  const { readable, reviewed, hasSummary } = status;
  const allRead = reviewed >= readable;
  if (readable === 0 || !(hasSummary || allRead)) return { label: "Check with AI", confirm: null };
  if (allRead) {
    return {
      label: "Check again",
      confirm: {
        count: readable,
        text: `Read ${photoCount(readable)} in this album again? It uses ${readable} of today's AI photos.`,
      },
    };
  }
  const unread = readable - reviewed;
  return {
    label: "Check again",
    confirm: {
      count: unread,
      text: `Read the ${unread === 1 ? "1 photo" : `${unread} photos`} in this album the AI has not read yet? It uses ${unread} of today's AI photos.`,
    },
  };
}

/**
 * "Check with AI" for one album, for a PM. While a check of the album is queued or running, its
 * progress shows instead. On an album already checked it is "Check again" and asks first.
 */
export function CheckWithAiButton({ projectId, albumId }: { projectId: string; albumId: string }) {
  const jobs = useActiveAiJobs(projectId, true);
  const status = useAlbumReviewStatus(albumId);
  const request = useRequestAlbumReview(projectId);
  const [asking, setAsking] = useState(false);
  const job = jobs.data?.active.find((j) => j.scope === "album" && j.albumId === albumId);
  if (job) return <AiJobProgress job={job} />;
  if (jobs.isError) {
    return (
      <ErrorState compact message="Could not load the AI checks" onRetry={() => jobs.refetch()} />
    );
  }
  if (status.isError) {
    return (
      <ErrorState
        compact
        message="Could not load this album's AI state"
        onRetry={() => status.refetch()}
      />
    );
  }

  const plan = status.data ? albumCheckPlan(status.data) : null;
  const blocked = blockingMessage(request.error);
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {/* A disabled button gets no pointer events, so the reason sits on its wrapper. */}
      <span title={blocked ?? undefined} className="inline-flex">
        <Button
          variant="outline"
          size="sm"
          data-testid="check-with-ai"
          disabled={request.isPending || jobs.isPending || !plan || isNotSetUp(request.error)}
          onClick={() => (plan?.confirm ? setAsking(true) : request.mutate(albumId))}
        >
          <Sparkles className="size-4" />
          {plan?.label ?? "Check with AI"}
        </Button>
      </span>
      {plan?.confirm && (
        <ConfirmDialog
          open={asking}
          onOpenChange={setAsking}
          title="Check this album again?"
          description={plan.confirm.text}
          onConfirm={() => {
            setAsking(false);
            request.mutate(albumId);
          }}
        />
      )}
      {blocked && (
        <span data-testid="check-with-ai-blocked" className="text-xs text-muted-foreground">
          {blocked}
        </span>
      )}
    </div>
  );
}
